/**
 * Centralized agent mappings for knowledge-graph components.
 * @remarks
 * The keys are the `status.agent` values the Ask Gemini SSE stream puts on the
 * wire. During the v1 → v2 cutover (SE-8432) this table covers BOTH emitters:
 *
 * - **v1** (`geminisportsai/player-graphrag-graph-bot`, `UnifiedStreamingLambda`
 *   behind CFN stack `<env>-SseStreamStack`): LangGraph node names — `decide`,
 *   `generateSQLWithLangChain`, `executeSQL`, `retrieveKB`, `fetchReports`,
 *   `knowledge`, `aggregate`, `incrementRetryCount`, `RoutingAgent`. Verified
 *   against the live stream on 2026-08-25 (SE-7078 / SE-7401). These keys are
 *   retained until v1 is decommissioned and are removed in a later ticket.
 * - **v2** (`geminisportsai/ask-gemini`, `agent-orchestrator`): the graph
 *   nodes `intent-classifier`, `tool-discovery`, `agent-reasoning`,
 *   `agent-synthesis`, `diagnostic`, the node-level `agent`, the
 *   `completion`-mode writer, and — for tool calls — the MCP tool name in
 *   camelCase (`executeSqlQuery`, `searchPlayers`, `getPlayer`, …). The tool
 *   set is open-ended, which is why the getters below fall back to a generic
 *   "Checking our records" for an unknown identifier instead of echoing the
 *   raw identifier to the user.
 *
 * Note also that v1's `incrementRetryCount` arrives with the raw message
 * `"start: incrementRetryCount"`; the label here is what the user actually
 * sees.
 *
 * When changing these, take the vocabulary from the emitting service or from a
 * capture of the live stream — never from a repository that merely looks like
 * the backend.
 */

/**
 * What the user sees for a `status.agent` value with no entry of its own.
 * @remarks
 * v2 reports MCP tool calls under the tool's camelCase name, and the tool set
 * grows without a frontend release. Every such call is a data lookup, so one
 * honest generic label covers them all; a raw `executeSqlQuery` on screen is
 * the failure this prevents.
 */
export const UNKNOWN_AGENT_LABEL = "Checking our records";

/**
 * Agent icon mapping for visual representation
 */
export const AGENT_ICONS: Record<string, string> = {
  // v1
  decide: "🔍",
  generateSQLWithLangChain: "🗄️",
  executeSQL: "⚡",
  retrieveKB: "📚",
  fetchReports: "📋",
  knowledge: "🌐",
  aggregate: "🤔",
  incrementRetryCount: "🔄",
  RoutingAgent: "🧭",
  // v2
  "intent-classifier": "🔍",
  "tool-discovery": "🗄️",
  "agent-reasoning": "🤔",
  "agent-synthesis": "✍️",
  diagnostic: "🩺",
  agent: "⚙️",
  completion: "✍️",
};

/**
 * Agent display names for user-friendly labels
 */
export const AGENT_DISPLAY_NAMES: Record<string, string> = {
  // v1
  decide: "Routing",
  generateSQLWithLangChain: "SQL Generation",
  executeSQL: "Database Query",
  retrieveKB: "Knowledge Search",
  fetchReports: "Report Lookup",
  knowledge: "Web Search",
  aggregate: "Answer Synthesis",
  incrementRetryCount: "Trying again",
  RoutingAgent: "Follow-up Routing",
  // v2
  "intent-classifier": "Routing",
  "tool-discovery": "Data Lookup",
  "agent-reasoning": "Reasoning",
  "agent-synthesis": "Answer Synthesis",
  diagnostic: "Diagnostics",
  agent: "Agent",
  completion: "Writing",
};

/**
 * Agent conversational names for progress messages
 */
export const AGENT_CONVERSATIONAL_NAMES: Record<string, string> = {
  // v1
  decide: "Understanding your question",
  generateSQLWithLangChain: "Finding the right data",
  executeSQL: "Checking our records",
  retrieveKB: "Searching knowledge base",
  fetchReports: "Looking up reports",
  knowledge: "Searching the web",
  aggregate: "Putting it all together",
  incrementRetryCount: "Trying again",
  RoutingAgent: "Understanding your follow-up",
  // v2
  "intent-classifier": "Understanding your question",
  "tool-discovery": "Finding the right data",
  "agent-reasoning": "Working out the answer",
  "agent-synthesis": "Putting it all together",
  diagnostic: "Checking what went wrong",
  agent: "Working on it",
  completion: "Writing",
};

/**
 * Get agent icon with fallback
 * @param agent - Agent identifier
 * @returns Icon emoji for the agent
 */
export const getAgentIcon = (agent: string): string => {
  return AGENT_ICONS[agent] || "🤖";
};

/**
 * Get agent display name with fallback
 * @param agent - Agent identifier
 * @returns Display name for the agent; {@link UNKNOWN_AGENT_LABEL} for an
 * identifier with no entry (never the raw identifier), and an empty string
 * for an empty identifier
 */
export const getAgentDisplayName = (agent: string): string => {
  if (!agent) {
    return "";
  }
  return AGENT_DISPLAY_NAMES[agent] || UNKNOWN_AGENT_LABEL;
};

/**
 * Get agent conversational name with fallback
 * @param agent - Agent identifier
 * @returns Conversational description for the agent;
 * {@link UNKNOWN_AGENT_LABEL} for an identifier with no entry (never the raw
 * identifier), and an empty string for an empty identifier
 */
export const getAgentConversationalName = (agent: string): string => {
  if (!agent) {
    return "";
  }
  return (
    AGENT_CONVERSATIONAL_NAMES[agent] ||
    AGENT_DISPLAY_NAMES[agent] ||
    UNKNOWN_AGENT_LABEL
  );
};
